import * as React from 'react';
import { cn } from '../../lib/utils';
import { useChartColors } from './use-chart-colors';

export interface DistributionBarSegment {
  label: string;
  value: number;
  /**
   * Explicit segment color. Omit to take the next slot of the canonical
   * colorblind-safe data-viz ramp in order (via {@link useChartColors}).
   */
  color?: string;
}

export interface DistributionBarProps {
  segments: DistributionBarSegment[];
  height?: number;
  /** Show legend chips below the bar. */
  showLegend?: boolean;
  className?: string;
}

/**
 * Tyndo DistributionBar — single-row stacked bar showing proportions across
 * a dimension (e.g. equipment by state, work orders by priority).
 *
 * Lighter than a donut, more compact, perfect for inline KPI rows. Segments
 * without an explicit `color` default to the canonical colorblind-safe data-viz
 * ramp via {@link useChartColors}, so the bar stays on-palette and theme-aware
 * out of the box; an explicit `color` per segment still wins.
 */
export function DistributionBar({
  segments,
  height = 8,
  showLegend = true,
  className,
}: DistributionBarProps) {
  const palette = useChartColors(Math.max(segments.length, 1));
  const colorFor = (seg: DistributionBarSegment, i: number) =>
    seg.color ?? palette[i % palette.length];
  const total = segments.reduce((s, seg) => s + Math.max(0, seg.value), 0);
  if (total === 0) {
    return (
      <div
        className={cn(
          'flex items-center gap-2 text-xs text-muted-foreground',
          className
        )}
      >
        <span
          aria-hidden
          className="block h-2 flex-1 rounded-full bg-surface-sunken"
          style={{ height }}
        />
        <span>No data</span>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div
        className="relative flex w-full overflow-hidden rounded-full bg-surface-sunken"
        style={{ height }}
        role="img"
        aria-label={`Distribution: ${segments
          .map((s) => `${s.label} ${Math.round((s.value / total) * 100)}%`)
          .join(', ')}`}
      >
        {segments.map((seg, i) => {
          const pct = (seg.value / total) * 100;
          if (pct <= 0) return null;
          return (
            <span
              key={seg.label}
              className="block h-full"
              style={{
                width: `${pct}%`,
                backgroundColor: colorFor(seg, i),
              }}
              title={`${seg.label}: ${seg.value}`}
            />
          );
        })}
      </div>
      {showLegend && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
          {segments.map((seg, i) => {
            const pct = Math.round((seg.value / total) * 100);
            return (
              <span
                key={seg.label}
                className="inline-flex items-center gap-1.5"
              >
                <span
                  aria-hidden
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: colorFor(seg, i) }}
                />
                <span className="text-muted-foreground">{seg.label}</span>
                <span className="t-tabular tabular-nums font-medium text-foreground">
                  {seg.value} ({pct}%)
                </span>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
